import { Component, EventEmitter, Input, Output } from '@angular/core';

@Component({
  selector: 'usb-media-banner',
  templateUrl: './usb-media-banner.component.html',
  styleUrls: ['./usb-media-banner.component.scss']
})
export class UsbMediaBannerComponent {
  /** Gallery images (slot 0, 1, 2). */
  @Input() images: Array<{ thumbnail: string }> = [];
  /** Video items for the third-slot switcher. */
  @Input() videos: Array<{ src: string }> = [];
  /** All media items mixed (image + video) for the selector row. */
  @Input() mediaItems: Array<{ type: 'image' | 'video'; src: string }> = [];
  /** Currently selected media for the third slot. */
  @Input() activeMedia: { type: 'image' | 'video'; src: string } | null = null;
  /** Index of the currently active media in the mediaItems array. */
  @Input() activeMediaIndex: number = -1;
  /** Whether to show placeholders when no images are available. */
  @Input() showPlaceholders: boolean = true;
  /** Total height of the banner in pixels. Side images are half of this. */
  @Input() height: number = 350;
  /** Optional margins to apply to the wrapper. */
  @Input() wrapperMargin: string = '0';

  /** Emitted when the user clicks an image slot (index in images array). */
  @Output() openImage = new EventEmitter<number>();
  /** Emitted when the user clicks a video slot (index in videos array). */
  @Output() openVideo = new EventEmitter<number>();
  /** Emitted when the user clicks a media selector button (index in mediaItems array, plus the media's disabled flag). */
  @Output() selectMedia = new EventEmitter<{ index: number; disabled: boolean }>();
  /** Emitted when the user clicks the "+ de N fotos" text. */
  @Output() seeMorePhotos = new EventEmitter<void>();
  /**
   * Emitted when the user clicks a media selector button of type 'video'.
   * Distinct from `selectMedia` so the parent can open the video lightbox
   * directly (useful when the banner is in singleImage mode and there's no
   * slot 2 to render the video into).
   */
  @Output() openVideoMedia = new EventEmitter<number>();

  get hasEnoughImages(): boolean {
    // Antes era >= 3. Lo bajamos a >= 2 para que con 2 imágenes se muestre la
    // estructura "1 grande + 1 chica" en vez de caer a singleImage full-width.
    return Array.isArray(this.images) && this.images.length >= 2;
  }

  get hasAnyImage(): boolean {
    return Array.isArray(this.images) && this.images.length > 0;
  }

  get sideImageHeight(): number {
    return Math.floor(this.height / 2);
  }

  get remainingPhotosCount(): number {
    return Math.max(0, this.images.length - 1);
  }

  /**
   * Texto del contador del overlay del banner: refleja el total de items
   * multimedia disponibles para explorar.
   *  - Solo videos → "+ de N video" / "+ de N videos"
   *  - Solo imágenes → "+ de N foto" / "+ de N fotos"  (N = imágenes restantes
   *    más allá de la que ya muestra el hero)
   *  - Mezcla (imágenes + videos) → "+ de N fotos · M videos"
   */
  get mediaItemsLabel(): string {
    const items = Array.isArray(this.mediaItems) ? this.mediaItems : [];
    if (items.length === 0) return '';
    const videoCount = items.filter((m: any) => m?.type === 'video').length;
    const imageCount = items.filter((m: any) => m?.type === 'image').length;

    if (imageCount > 0 && videoCount > 0) {
      const fotoTxt = `+ de ${imageCount} foto${imageCount === 1 ? '' : 's'}`;
      const videoTxt = `${videoCount} video${videoCount === 1 ? '' : 's'}`;
      return `${fotoTxt} · ${videoTxt}`;
    }
    if (videoCount > 0) {
      return `+ de ${videoCount} video${videoCount === 1 ? '' : 's'}`;
    }
    // Solo imágenes: N = las que faltan ver además del hero
    const remaining = Math.max(0, this.images.length - 1);
    return `+ de ${remaining} foto${remaining === 1 ? '' : 's'}`;
  }
}
